import axios from 'axios';
import {
  addBreadcrumb as sentryAddBreadcrumb,
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
  withScope,
} from '@sentry/core';
import type { Extras } from '@sentry/core';

import { extractApiErrorTags } from './apiErrorTags';
import { isTokenRefreshError } from '../services/tokenRefreshError';

interface CaptureExceptionParams {
  error: Error | unknown;
  extra?: Extras;
  tags?: Record<string, string>;
}

interface CaptureMessageParams {
  message: string;
  extra?: Extras;
}

interface BreadcrumbParams {
  message: string;
  category?: string;
  data?: Record<string, unknown>;
}

export const isRequestCanceled = (error: unknown): boolean =>
  axios.isCancel(error) ||
  (error != null && typeof error === 'object' && (
    (error as { name?: string }).name === 'AbortError' ||
    (error as { code?: string }).code === 'ERR_CANCELED'
  ));

const capture = ({ error, extra = {}, tags }: CaptureExceptionParams): void => {
  let errorToCapture: Error;

  if (!(error instanceof Error)) {
    errorToCapture = error instanceof Object
      ? new Error(JSON.stringify(error))
      : new Error(String(error));
  } else {
    errorToCapture = error;
  }

  // Read the tags from the original error: wrapping non-Error values above
  // loses the axios config the tags are derived from.
  const apiTags = extractApiErrorTags(error);
  const mergedTags = { ...(apiTags ?? {}), ...(tags ?? {}) };

  if (Object.keys(mergedTags).length > 0) {
    withScope(scope => {
      scope.setTags(mergedTags);
      sentryCaptureException(errorToCapture, { extra });
    });
    return;
  }

  sentryCaptureException(errorToCapture, { extra });
};

export const captureException = (params: CaptureExceptionParams): void => {
  if (isTokenRefreshError(params.error) || isRequestCanceled(params.error)) return;

  capture(params);
};

export const captureTokenRefreshError = (params: CaptureExceptionParams): void => {
  capture(params);
};

export const captureMessage = ({ message, extra = {} }: CaptureMessageParams): void => {
  sentryCaptureMessage(message, { extra });
};

export const addBreadcrumb = ({ message, category = 'auth', data }: BreadcrumbParams): void => {
  sentryAddBreadcrumb({ level: 'info', category, message, data });
};
